import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { japanPrefectures } from '../mocks/japanPrefectures';

interface PrefectureCounts {
  [prefectureId: string]: {
    artistCount: number;
    shopCount: number;
  };
}

// 都道府県名（日本語）→ 都道府県ID のマッピング
const PREF_NAME_TO_ID: Record<string, string> = {
  '北海道': 'hokkaido',
  '青森県': 'aomori', '岩手県': 'iwate', '宮城県': 'miyagi', '秋田県': 'akita',
  '山形県': 'yamagata', '福島県': 'fukushima',
  '茨城県': 'ibaraki', '栃木県': 'tochigi', '群馬県': 'gunma', '埼玉県': 'saitama',
  '千葉県': 'chiba', '東京都': 'tokyo', '神奈川県': 'kanagawa',
  '新潟県': 'niigata', '富山県': 'toyama', '石川県': 'ishikawa', '福井県': 'fukui',
  '山梨県': 'yamanashi', '長野県': 'nagano', '岐阜県': 'gifu', '静岡県': 'shizuoka',
  '愛知県': 'aichi',
  '三重県': 'mie', '滋賀県': 'shiga', '京都府': 'kyoto', '大阪府': 'osaka',
  '兵庫県': 'hyogo', '奈良県': 'nara', '和歌山県': 'wakayama',
  '鳥取県': 'tottori', '島根県': 'shimane', '岡山県': 'okayama', '広島県': 'hiroshima',
  '山口県': 'yamaguchi', '徳島県': 'tokushima', '香川県': 'kagawa', '愛媛県': 'ehime',
  '高知県': 'kochi',
  '福岡県': 'fukuoka', '佐賀県': 'saga', '長崎県': 'nagasaki', '熊本県': 'kumamoto',
  '大分県': 'oita', '宮崎県': 'miyazaki', '鹿児島県': 'kagoshima', '沖縄県': 'okinawa',
  // 「県」なし表記にも対応
  '東京': 'tokyo', '大阪': 'osaka', '京都': 'kyoto',
  '奈良': 'nara', '兵庫': 'hyogo', '滋賀': 'shiga', '三重': 'mie', '和歌山': 'wakayama',
};

/**
 * 都道府県名文字列からIDを解決するヘルパー
 * 完全一致 → 部分一致の順で試みる
 */
function resolvePrefId(prefName: string): string | null {
  if (!prefName) return null;
  const trimmed = prefName.trim();
  if (PREF_NAME_TO_ID[trimmed]) return PREF_NAME_TO_ID[trimmed];
  // 部分一致（例: "奈良" → "nara"）
  for (const [key, id] of Object.entries(PREF_NAME_TO_ID)) {
    if (trimmed.includes(key) || key.includes(trimmed)) return id;
  }
  // japanPrefectures の name フィールドとも照合
  const found = japanPrefectures.find(
    (p) => p.name === trimmed || trimmed.includes(p.name) || p.name.includes(trimmed)
  );
  return found?.id ?? null;
}

/**
 * 全都道府県の登録アーティスト数・ショップ数をDBから取得して返すフック
 */
export function usePrefectureCounts() {
  const [counts, setCounts] = useState<PrefectureCounts>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      setLoading(true);
      try {
        // ── アーティスト数 ──────────────────────────────────────────
        const { data: layers, error: layerError } = await supabase
          .from('artist_layers')
          .select('user_id, prefecture');
        if (layerError) throw layerError;

        // makerタイプのプロフィールのみ対象
        const artistUserIds = (layers || []).map((l) => l.user_id).filter(Boolean);
        let makerIds = new Set<string>();
        if (artistUserIds.length > 0) {
          const { data: profiles, error: profileError } = await supabase
            .from('profiles')
            .select('id, user_type')
            .in('id', artistUserIds)
            .eq('user_type', 'maker');
          if (profileError) throw profileError;
          makerIds = new Set((profiles || []).map((p) => p.id));
        }

        const artistCountById: Record<string, number> = {};
        (layers || []).forEach((layer) => {
          if (!layer.prefecture || !makerIds.has(layer.user_id)) return;
          const id = resolvePrefId(layer.prefecture);
          if (id) artistCountById[id] = (artistCountById[id] || 0) + 1;
        });

        // ── ショップ数 ──────────────────────────────────────────────
        const { data: shopLayers, error: shopError } = await supabase
          .from('shop_layers')
          .select('user_id, prefecture');
        if (shopError) throw shopError;

        const shopCountById: Record<string, number> = {};
        (shopLayers || []).forEach((shop) => {
          if (!shop.prefecture) return;
          const id = resolvePrefId(shop.prefecture);
          if (id) shopCountById[id] = (shopCountById[id] || 0) + 1;
        });

        // ── 都道府県ごとに集計 ──────────────────────────────────────
        const result: PrefectureCounts = {};
        japanPrefectures.forEach((pref) => {
          result[pref.id] = {
            artistCount: artistCountById[pref.id] ?? 0,
            shopCount: shopCountById[pref.id] ?? 0,
          };
        });

        setCounts(result);
      } catch (err) {
        console.error('Failed to fetch prefecture counts:', err);
        // フォールバック: 全て0
        const fallback: PrefectureCounts = {};
        japanPrefectures.forEach((pref) => {
          fallback[pref.id] = { artistCount: 0, shopCount: 0 };
        });
        setCounts(fallback);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, []);

  return { counts, loading };
}